import React from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ArrowRight } from "lucide-react";
import { LegalLayout } from "../components/LegalLayout";
import {
  APP_NAME, SUPPORT_EMAIL, LEGAL_ROUTES,
} from "../lib/legal";

// Public informational page — /delete-account
// Required by App Store / Play Store review: any user must be able to find how
// to delete their account without needing to be logged in. This page NEVER
// performs a deletion — the real destructive action lives at /account/delete
// (protected). Harmonised to reuse LegalLayout (shared header, footer nav, SEO).
export default function DeleteAccountPublic() {
  return (
    <LegalLayout
      title="Suppression de compte"
      description={`Comment supprimer votre compte ${APP_NAME} et l'ensemble de vos données.`}
      pathname={LEGAL_ROUTES.deleteAccount}
    >
      <div
        data-testid="public-delete-account-page"
        style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}
      >
        <span style={{
          width: 42, height: 42, borderRadius: 12,
          background: "rgba(220, 38, 38, 0.10)",
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>
          <ShieldAlert size={20} color="#dc2626" />
        </span>
        <p style={{ margin: 0, fontSize: 15, color: "var(--mf-text-secondary)", lineHeight: 1.5 }}>
          Vous pouvez supprimer votre compte à tout moment depuis l&apos;application, en
          quelques secondes. Aucune demande écrite n&apos;est nécessaire.
        </p>
      </div>

      <section style={{ marginTop: 24 }}>
        <h2 style={{ margin: "0 0 10px", fontSize: 18, fontWeight: 700, color: "var(--mf-text)" }}>
          Comment procéder
        </h2>
        <ol style={{ margin: 0, paddingLeft: 22, lineHeight: 1.75 }}>
          <li>Connectez-vous à {APP_NAME} avec votre email ou votre compte Google.</li>
          <li>Ouvrez l&apos;onglet <strong>Profil</strong>.</li>
          <li>Dans la section <strong>Zone dangereuse</strong>, choisissez <strong>Supprimer mon compte</strong>.</li>
          <li>Confirmez votre intention en tapant <code style={{
            background: "rgba(11,25,44,0.06)", padding: "1px 6px", borderRadius: 4,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 700,
          }}>SUPPRIMER</code>.</li>
          <li>Reconfirmez votre identité (mot de passe ou Google).</li>
        </ol>
      </section>

      <section style={{ marginTop: 24 }}>
        <h2 style={{ margin: "0 0 10px", fontSize: 18, fontWeight: 700, color: "var(--mf-text)" }}>
          Ce qui est supprimé
        </h2>
        <ul style={{ margin: 0, paddingLeft: 22, lineHeight: 1.75 }}>
          <li>Les données de votre compte (identifiants, profil, préférences).</li>
          <li>Tous vos véhicules, leurs photos et leurs informations techniques.</li>
          <li>Toutes vos interventions, entretiens et l&apos;historique de kilométrage.</li>
          <li>Vos entretiens personnalisés.</li>
          <li>Toutes les pièces jointes que vous avez téléversées (factures, cartes grises, assurances, etc.).</li>
          <li>Les factures scannées et leurs brouillons.</li>
          <li>Vos liens de partage publics (immédiatement révoqués et invalides).</li>
          <li>Vos abonnements aux notifications Web Push.</li>
          <li>Vos préférences de notification.</li>
          <li>Votre journal d&apos;activité interne.</li>
          <li>Les jetons de réinitialisation de mot de passe encore actifs, s&apos;il y en a.</li>
        </ul>
        <p style={{ margin: "12px 0 0", fontSize: 13, color: "var(--mf-text-muted)", lineHeight: 1.55 }}>
          L&apos;accès au compte est supprimé ou rendu inaccessible rapidement,
          et les données actives associées sont effacées selon le
          fonctionnement technique du service. Certaines copies résiduelles
          peuvent toutefois subsister temporairement dans les sauvegardes,
          journaux techniques ou systèmes de reprise. Selon les informations
          communiquées par Emergent, la suppression permanente des données
          personnelles intervient au plus tard <strong>45 jours</strong> après
          confirmation de la demande, sauf obligation légale, nécessité de
          sécurité, prévention de la fraude ou défense de droits en justice.
        </p>
      </section>

      <section style={{ marginTop: 24 }}>
        <h2 style={{ margin: "0 0 10px", fontSize: 18, fontWeight: 700, color: "var(--mf-text)" }}>
          Vous avez besoin d&apos;aide ?
        </h2>
        <p style={{ margin: 0, lineHeight: 1.6 }}>
          Écrivez à <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
        </p>
      </section>

      <div style={{ marginTop: 28 }}>
        <Link
          to="/login"
          data-testid="public-delete-goto-login"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "12px 22px", borderRadius: 999,
            background: "var(--mf-brand)", color: "#fff",
            textDecoration: "none", fontWeight: 700, fontSize: 15,
          }}
        >
          Se connecter pour supprimer <ArrowRight size={16} />
        </Link>
      </div>
    </LegalLayout>
  );
}
